import { initializeApp } from "firebase/app";
import {
  getAuth,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  type User,
} from "firebase/auth";
import { config, initConfig } from "./config.ts";
import { notify } from "./notifications.ts";

const firebaseConfig = {
  apiKey: "AIzaSyAyFYDi_TOaCZdifXXO7p87a4-Pam4Uo_I",
  authDomain: "servernetfun.firebaseapp.com",
  projectId: "servernetfun",
  storageBucket: "servernetfun.firebasestorage.app",
  messagingSenderId: "1044956248130",
  appId: "1:1044956248130:web:989e8723a2e84ecaf295db",
};
initConfig();
export const APP = initializeApp(firebaseConfig);
const auth = getAuth(APP);
auth.tenantId = config.tenantId || null;

let currentUser: User | null = null;

export function getCurrentUser() {
  return currentUser;
}

export function isLoggedIn() {
  return !!currentUser;
}

export async function getAuthToken(): Promise<string | null> {
  return currentUser ? currentUser.getIdToken() : null;
}

export async function loginWithPassword(
  email: string,
  password: string,
): Promise<User> {
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password,
    );

    // Ottieni ID Token e mandalo al backend
    // return await userCredential.user.getIdToken();
    currentUser = userCredential.user;
    notify(
      `Benvenuto ${currentUser.displayName || currentUser.email || currentUser.uid}`,
      "success",
    );
    return currentUser;
  } catch (err) {
    notify("Logi non valido", "error");
    throw err;
  }
}

export async function sendPasswordReset(
  email: string | null | undefined,
): Promise<void> {
  const normalizedEmail = (email ?? "").trim();
  if (!normalizedEmail) {
    notify("Inserisci un indirizzo email valido", "error");
    throw new Error("Missing email");
  }

  try {
    await sendPasswordResetEmail(auth, normalizedEmail);
    notify(
      "Ti abbiamo inviato un'email con le istruzioni per reimpostare la password",
      "success",
    );
  } catch (err) {
    const errorCode =
      typeof err === "object" && err !== null && "code" in err
        ? String(err.code)
        : "";

    switch (errorCode) {
      case "auth/invalid-email":
        notify("L'indirizzo email non e' valido", "error");
        break;
      case "auth/too-many-requests":
        notify("Troppi tentativi. Riprova tra qualche minuto", "error");
        break;
      default:
        notify(
          "Non e' stato possibile inviare l'email di recupero password",
          "error",
        );
    }
    throw err;
  }
}

export async function checkIdToken(user: User): Promise<true> | never {
  currentUser = user;
  const idToken = await user.getIdToken();
  if (!idToken) {
    throw new Error("User is not authenticated");
  }
  const res = await fetch("/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ idToken }),
  }).then(async (res) => {
    if (!res.ok) {
      throw new Error("Token validation failed");
    }
    return (await res.json()) as { success: boolean; error?: string | null };
  });
  if (res.success) {
    return true;
  } else if (res.error) {
    throw new Error(res.error);
  } else {
    throw new Error("Unknown error during token validation");
  }
}

export async function logout() {
  await auth.signOut();
  currentUser = null;

  try {
    await fetch("/logout", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    });
    return true;
  } catch (err) {
    console.error("Logout request failed:", err);
    return false;
  }
}

export async function redirectOnLogin() {
  try {
    const redirectUrl = new URL(window.location.href).searchParams.get(
      "redirect",
    );
    if (
      redirectUrl &&
      typeof redirectUrl === "string" &&
      redirectUrl.startsWith("/") &&
      window.location.href === "location"
    ) {
      window.location.href = redirectUrl;
    } else if (redirectUrl) {
      const url = new URL(redirectUrl, window.location.href);
      if (url.hostname === window.location.hostname) {
        window.location.href = url.href;
      } else {
        window.location.href = "/";
      }
    } else {
      window.location.href = "/";
    }
  } catch {
    window.location.href = "/";
  }
}

const setCurrentUser = async (user: User | null) => {
  let evt: CustomEvent<{ userRecord: User | null }>;
  if (user) {
    try {
      const res = await checkIdToken(user);

      if (!res) {
        return await setCurrentUser(null);
      }
      const eventData = {
        detail: {
          userRecord: user,
        },
      };
      evt = new CustomEvent("login", eventData);
    } catch (err) {
      console.error("Backend authentication failed:", err);
      throw err;
    }
  } else {
    await logout();
    evt = new CustomEvent("logout", { detail: { userRecord: null } });
  }
  document.querySelectorAll(`[data-on\\:${evt.type}]`).forEach((el) => {
    el.dispatchEvent(evt);
  });
  window.dispatchEvent(evt);
};
onAuthStateChanged(auth, async (user) => {
  await setCurrentUser(user);
});
